import { useCallback, useRef, useState } from "react";
import Calendar from "./calendar";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { formateDateShort } from "../../../utils/formater";
import ORMfunctions from "../../../hooks/app/general";
import TimePicker from "./timepicker";
import { useKeydownListener } from "../../../hooks/useKeydownListener";

function DatepickerInput(props: any) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dateStartRef = useRef<HTMLDivElement>(null);
  const dateEndRef = useRef<HTMLDivElement>(null);
  const timeStartRef = useRef<HTMLDivElement>(null);
  const timeendRef = useRef<HTMLDivElement>(null);
  const [showCalendar, setShowCalendar] = useState<any>(false);
  const [dateType, setDateType] = useState<any>("start");
  const startDate = useSelector(
    (state: RootState) => state.userSession.startDate,
  );
  const endDate = useSelector((state: RootState) => state.userSession.endDate);
  const startTime = useSelector(
    (state: RootState) => state.userSession.startTime,
  );
  const endTime = useSelector((state: RootState) => state.userSession.endTime);

  const [opentimeStart, setOpentimeStart] = useState<any>(false);
  const [opentimeEnd, setOpentimeEnd] = useState<any>(false);

  // Keyboard navigation
  const handleKeydown = useCallback((event: KeyboardEvent) => {
    const activeElement = document.activeElement;

    switch (event.key) {
      case "ArrowUp":
      case "ArrowDown":
      case "Enter":
        event.preventDefault();
        // Execute the corresponding function based on which ref is focused
        if (activeElement === dateStartRef.current) {
          setShowCalendar(true);
          setDateType("start");
        } else if (activeElement === dateEndRef.current) {
          setShowCalendar(true);
          setDateType("end");
        } else if (activeElement === timeStartRef.current) {
          setOpentimeStart(true);
        } else if (activeElement === timeendRef.current) {
          setOpentimeEnd(true);
        }
        break;

      case "Escape":
        setShowCalendar(false);
        setOpentimeStart(false);
        setOpentimeEnd(false);
        break;

      case "Tab":
        // Allow natural tab navigation
        break;

      default:
        break;
    }
  }, []);

  useKeydownListener(containerRef, handleKeydown);

  return (
    <div
      className="orm-datepicker orm-datepicker--wrappless orm-relative"
      ref={containerRef}
    >
      {props.type === "modal" ? (
        <Calendar
          show={showCalendar}
          type={dateType}
          singleMonth={true}
          onTypeChange={(value: string) => {
            setDateType(value);
          }}
          onDone={() => {
            setShowCalendar(false);
            setTimeout(() => {
              if (dateType === "start") dateStartRef.current?.focus();
              if (dateType === "end") dateEndRef.current?.focus();
            }, 10);
          }}
        />
      ) : (
        <></>
      )}
      <div className="orm-datecol orm-pr-sm-8">
        <div className="orm-searchbar__headline">
          <strong>{ORMfunctions.i18n("general/pickup") || "Abholung"}</strong>
        </div>
        <div className="orm-dateselect orm-row no-margin">
          <div
            className={`orm-col-xs-6 orm-col-md-6 orm-px-8 orm-datepicker--start ${showCalendar && dateType === "start" ? "orm-datepicker-active" : ""}`}
            tabIndex={0}
            onClick={() => {
              setShowCalendar(true);
              setDateType("start");
            }}
            ref={dateStartRef}
            role="button"
            aria-label={
              ORMfunctions.i18n("general/pickup_date") || "Abholdatum auswählen"
            }
            aria-haspopup="dialog"
            aria-expanded={showCalendar && dateType === "start"}
          >
            <div>
              <small>{ORMfunctions.i18n("general/date") || "DATUM"}</small>
            </div>
            <div className="orm-datepicker__input orm-pr-16">
              {startDate ? formateDateShort(startDate!) : "__.__.____"}
              <i className="fal fa-calendar-alt" aria-hidden="true"></i>
            </div>
          </div>
          <div
            className={`orm-col-xs-6 orm-col-md-6 orm-px-8 orm-relative orm-timepicker--start ${opentimeStart ? "orm-timepicker-active" : ""}`}
            tabIndex={0}
            ref={timeStartRef}
            role="button"
            aria-label={
              ORMfunctions.i18n("general/pickup_time") || "Abholzeit auswählen"
            }
            aria-haspopup="dialog"
            aria-expanded={opentimeStart}
          >
            <div
              onClick={() => {
                opentimeStart
                  ? setOpentimeStart(false)
                  : setOpentimeStart(true);
                setShowCalendar(false);
                setOpentimeEnd(false);
              }}
            >
              <div>
                <small>{ORMfunctions.i18n("general/time") || "UHRZEIT"}</small>
              </div>
              <div>
                <div className="orm-timepicker__input">
                  <span>{startTime}</span>{" "}
                  {ORMfunctions.i18n("general/hour") || "Uhr"}{" "}
                  <i className="fal fa-clock" aria-hidden="true"></i>
                </div>
              </div>
            </div>
            {opentimeStart ? (
              <TimePicker
                type="start"
                onTimeChange={(value) => {
                  setOpentimeStart(false);
                }}
              />
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      <div className="orm-datecol orm-pl-sm-8">
        <div className="orm-searchbar__headline">
          <strong>{ORMfunctions.i18n("general/delivery") || "Abgabe"}</strong>
        </div>
        <div className="orm-dateselect orm-row no-margin">
          <div
            className={`orm-col-xs-6 orm-col-md-6 orm-px-8 orm-datepicker--end ${showCalendar && dateType === "end" ? "orm-datepicker-active" : ""}`}
            tabIndex={0}
            onClick={() => {
              setShowCalendar(true);
              setDateType("end");
            }}
            ref={dateEndRef}
            role="button"
            aria-label={
              ORMfunctions.i18n("general/delivery_date") ||
              "Abgabedatum auswählen"
            }
            aria-haspopup="dialog"
            aria-expanded={showCalendar && dateType === "end"}
          >
            <div>
              <small>{ORMfunctions.i18n("general/date") || "DATUM"}</small>
            </div>
            <div
              className={
                "orm-datepicker__input orm-datepicker__input--end orm-pr-16"
              }
            >
              {endDate ? formateDateShort(endDate!) : "__.__.____"}
              <i className="fal fa-calendar-alt" aria-hidden="true"></i>
            </div>
          </div>
          <div
            className={`orm-col-xs-6 orm-col-md-6 orm-px-8 orm-relative orm-timepicker--end ${opentimeEnd ? "orm-timepicker-active" : ""}`}
            tabIndex={0}
            ref={timeendRef}
            role="button"
            aria-label={
              ORMfunctions.i18n("general/delivery_time") ||
              "Abgabezeit auswählen"
            }
            aria-haspopup="dialog"
            aria-expanded={opentimeEnd}
          >
            <div
              onClick={() => {
                opentimeEnd ? setOpentimeEnd(false) : setOpentimeEnd(true);
                setShowCalendar(false);
                setOpentimeStart(false);
              }}
            >
              <div>
                <small>{ORMfunctions.i18n("general/time") || "UHRZEIT"}</small>
              </div>
              <div>
                <div className="orm-timepicker__input">
                  <span>{endTime}</span>{" "}
                  {ORMfunctions.i18n("general/hour") || "Uhr"}{" "}
                  <i className="fal fa-clock" aria-hidden="true"></i>
                </div>
              </div>
            </div>
            {opentimeEnd ? (
              <TimePicker
                type="end"
                onTimeChange={(value) => {
                  // console.log("change End", value);
                  setOpentimeEnd(false);
                }}
              />
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      {props.type !== "modal" ? (
        <Calendar
          show={showCalendar}
          type={dateType}
          singleMonth={false}
          onTypeChange={(value: string) => {
            setDateType(value);
          }}
          onDone={() => {
            setShowCalendar(false);
            setTimeout(() => {
              if (dateType === "start") dateStartRef.current?.focus();
              if (dateType === "end") dateEndRef.current?.focus();
            }, 10);
          }}
        />
      ) : (
        <></>
      )}
    </div>
  );
}

export default DatepickerInput;
